import {type MoneyResponseBody} from '@online-offer/shared';

import {isNil, isNotNil, uniq} from 'ramda';

import {type Nullish} from 'shared/web';

import {
  OnlineOfferAlternative,
  OnlineOfferAlternativeAvailability,
  OnlineOfferAlternativeCondition,
  OnlineOfferAlternativeKind,
  OnlineOfferCustomerChoice,
  OnlineOfferEquipmentCategory,
} from '../api/types/onlineOfferAlternativesTypes';

/**
 * Pure logic of the customer comparison page: ordering, the reference alternative, price deltas,
 * the row matrix and its differences. Values stay raw (money, dates, enums) so the view formats
 * them; two cells are "the same" when their raw values are equal.
 */

export type ComparisonGroup = 'price' | 'vehicle' | 'equipment';

export type ComparisonValue =
  | {type: 'money'; money: MoneyResponseBody | null; isNegative?: boolean}
  /** Amount only: it can mix the vehicle's rate with the upsell lines' own rates. */
  | {type: 'vat'; money: MoneyResponseBody | null}
  | {type: 'accessories'; money: MoneyResponseBody | null; names: string[]}
  | {type: 'text'; text: string | null}
  | {type: 'date'; date: string | null}
  | {type: 'power'; kw: number | null}
  | {type: 'mileage'; km: number | null}
  | {type: 'vin'; vin: string | null; kind: OnlineOfferAlternativeKind}
  | {type: 'source'; kind: OnlineOfferAlternativeKind; condition: OnlineOfferAlternativeCondition}
  | {type: 'availability'; availability: OnlineOfferAlternativeAvailability}
  | {
      type: 'equipment';
      status: OnlineOfferEquipmentCategory | 'NOT_LISTED';
      packageName: string | null;
      price: MoneyResponseBody | null;
    };

export type ComparisonRow = {
  id: string;
  group: ComparisonGroup;
  /** Translation key for fixed rows; equipment rows carry the item name instead. */
  labelKey: string | null;
  label: string | null;
  /** One value per alternative, in the order the alternatives were passed in. */
  values: ComparisonValue[];
};

const toCents = (amount: string) => Math.round(Number(amount) * 100);
const fromCents = (cents: number) => (cents / 100).toFixed(2);

/** "A" < "B" < … < "Z" < "AA": letters are never reused, so the order is stable. */
export const sortByLetter = (alternatives: OnlineOfferAlternative[]) =>
  [...alternatives].sort(
    (a, b) => a.letter.length - b.letter.length || a.letter.localeCompare(b.letter)
  );

/** Applied alternative whose live price was cleared (PRICE_MISSING) has no total to compare. */
export const hasTotal = (alternative: OnlineOfferAlternative) => isNotNil(alternative.total);

/**
 * Default reference = an alternative the customer can still choose: the applied one (the vehicle
 * the offer page shows), else the current choice, else the first selectable by letter. Only when
 * none is selectable does the first one with a total stand in. An expired or otherwise
 * unavailable alternative is no price to measure the others against, and one without a total
 * cannot be a reference at all.
 */
export const getDefaultReferenceId = (
  alternatives: OnlineOfferAlternative[],
  customerChoice: OnlineOfferCustomerChoice | Nullish
) => {
  const sorted = sortByLetter(alternatives).filter(hasTotal);
  const selectable = sorted.filter((alternative) => alternative.selectable);
  const chosenId = customerChoice?.isCurrent ? customerChoice.alternativeId : null;

  return (
    (
      selectable.find((alternative) => alternative.isApplied) ??
      selectable.find((alternative) => alternative.id === chosenId) ??
      selectable[0] ??
      sorted[0]
    )?.id ?? null
  );
};

/**
 * Total incl. VAT of `alternative` minus the reference's. `null` for the reference itself and
 * whenever either side has no total.
 */
export const getDeltaVsReference = (
  alternative: OnlineOfferAlternative,
  reference: OnlineOfferAlternative
): MoneyResponseBody | null =>
  alternative.id === reference.id || isNil(alternative.total) || isNil(reference.total)
    ? null
    : {
        amount: fromCents(
          toCents(alternative.total.withVat.amount) - toCents(reference.total.withVat.amount)
        ),
        currency: alternative.total.withVat.currency,
      };

const sumUpsell = (alternative: OnlineOfferAlternative): MoneyResponseBody | null => {
  const [firstLine] = alternative.upsell;
  if (isNil(firstLine)) {
    return null;
  }

  return {
    amount: fromCents(
      alternative.upsell.reduce((sum, line) => sum + toCents(line.sellingPrice.withVat.amount), 0)
    ),
    currency: firstLine.sellingPrice.withVat.currency,
  };
};

const joinDefined = (parts: (string | null)[]) => {
  const defined = parts.filter(isNotNil);

  return defined.length > 0 ? defined.join(' / ') : null;
};

const fixedRow = (
  id: string,
  group: ComparisonGroup,
  alternatives: OnlineOfferAlternative[],
  toValue: (alternative: OnlineOfferAlternative) => ComparisonValue
): ComparisonRow => ({
  id,
  group,
  labelKey: `alternatives.comparison.rows.${id}`,
  label: null,
  values: alternatives.map(toValue),
});

const EQUIPMENT_CATEGORY_ORDER: OnlineOfferEquipmentCategory[] = ['STANDARD', 'OPTION', 'PACKAGE'];

const buildEquipmentRows = (alternatives: OnlineOfferAlternative[]): ComparisonRow[] => {
  const firstSeen = new Map<string, {name: string; category: OnlineOfferEquipmentCategory}>();
  alternatives.forEach((alternative) =>
    alternative.equipment.forEach((item) => {
      if (!firstSeen.has(item.key)) {
        firstSeen.set(item.key, {name: item.name, category: item.category});
      }
    })
  );

  return [...firstSeen.entries()]
    .sort(
      ([, a], [, b]) =>
        EQUIPMENT_CATEGORY_ORDER.indexOf(a.category) -
          EQUIPMENT_CATEGORY_ORDER.indexOf(b.category) || a.name.localeCompare(b.name)
    )
    .map(([key, {name}]) => ({
      id: `equipment:${key}`,
      group: 'equipment' as const,
      labelKey: null,
      label: name,
      values: alternatives.map((alternative): ComparisonValue => {
        const item = alternative.equipment.find((candidate) => candidate.key === key);

        return {
          type: 'equipment',
          status: item?.category ?? 'NOT_LISTED',
          packageName: item?.packageName ?? null,
          price: item?.price ?? null,
        };
      }),
    }));
};

export const buildComparisonRows = (alternatives: OnlineOfferAlternative[]): ComparisonRow[] => {
  const hasUsed = alternatives.some((alternative) => alternative.condition === 'USED');
  const hasCustomerNote = alternatives.some((alternative) => isNotNil(alternative.customerNote));

  const price = [
    fixedRow('vehiclePrice', 'price', alternatives, (a) => ({
      type: 'money',
      money: a.vehiclePrice?.withVat ?? null,
    })),
    fixedRow('discount', 'price', alternatives, (a) => ({
      type: 'money',
      money: a.discount?.withVat ?? null,
      isNegative: true,
    })),
    fixedRow('accessories', 'price', alternatives, (a) => ({
      type: 'accessories',
      money: sumUpsell(a),
      names: a.upsell.map((line) => line.name),
    })),
    fixedRow('totalWithoutVat', 'price', alternatives, (a) => ({
      type: 'money',
      money: a.total?.withoutVat ?? null,
    })),
    fixedRow('vat', 'price', alternatives, (a) => ({
      type: 'vat',
      money: a.total?.vat ?? null,
    })),
  ];

  const vehicle = [
    fixedRow('source', 'vehicle', alternatives, (a) => ({
      type: 'source',
      kind: a.kind,
      condition: a.condition,
    })),
    fixedRow('availability', 'vehicle', alternatives, (a) => ({
      type: 'availability',
      availability: a.availability,
    })),
    fixedRow('delivery', 'vehicle', alternatives, (a) => ({type: 'text', text: a.deliveryNote})),
    ...(hasCustomerNote
      ? [
          fixedRow('customerNote', 'vehicle', alternatives, (a) => ({
            type: 'text',
            text: a.customerNote,
          })),
        ]
      : []),
    fixedRow('vin', 'vehicle', alternatives, (a) => ({type: 'vin', vin: a.vin, kind: a.kind})),
    fixedRow('validUntil', 'vehicle', alternatives, (a) => ({type: 'date', date: a.validUntil})),
    ...(hasUsed
      ? [
          fixedRow('firstRegistration', 'vehicle', alternatives, (a) => ({
            type: 'date',
            date: a.specification.firstRegistration,
          })),
          fixedRow('mileage', 'vehicle', alternatives, (a) => ({
            type: 'mileage',
            km: a.specification.mileage,
          })),
        ]
      : []),
    fixedRow('engine', 'vehicle', alternatives, (a) => ({
      type: 'text',
      text: a.specification.engine,
    })),
    fixedRow('power', 'vehicle', alternatives, (a) => ({
      type: 'power',
      kw: a.specification.powerKw,
    })),
    fixedRow('fuel', 'vehicle', alternatives, (a) => ({
      type: 'text',
      text: a.specification.fuelType,
    })),
    fixedRow('transmissionDrive', 'vehicle', alternatives, (a) => ({
      type: 'text',
      text: joinDefined([a.specification.transmission, a.specification.drive]),
    })),
    fixedRow('exteriorColor', 'vehicle', alternatives, (a) => ({
      type: 'text',
      text: a.specification.exteriorColor,
    })),
    fixedRow('interiorColor', 'vehicle', alternatives, (a) => ({
      type: 'text',
      text: a.specification.interiorColor,
    })),
  ];

  return [...price, ...vehicle, ...buildEquipmentRows(alternatives)];
};

const valueKey = (value: ComparisonValue | undefined) => JSON.stringify(value);

export const isRowDiffering = (row: ComparisonRow) => uniq(row.values.map(valueKey)).length > 1;

/** A cell is highlighted when its row differs and it does not match the reference column. */
export const isCellDifferentFromReference = (
  row: ComparisonRow,
  index: number,
  referenceIndex: number
) =>
  isRowDiffering(row) && valueKey(row.values.at(index)) !== valueKey(row.values.at(referenceIndex));

export const countDifferingRows = (rows: ComparisonRow[]) => rows.filter(isRowDiffering).length;

export const filterRows = (rows: ComparisonRow[], isOnlyDifferences: boolean) =>
  isOnlyDifferences ? rows.filter(isRowDiffering) : rows;
